/**
 * /settings/technicians (spec 016 US3) — cadastro de técnicos de campo: nome,
 * telefone, documento, cidades que atende, capacidade diária, ausências,
 * ativo e observações. Leitura para todo o staff (a escolha na OS e no ticket
 * usa a mesma lista); escrita só do admin — a API barra, o front esconde os
 * controles. Desativar não apaga: o técnico some da escolha e continua nas OS
 * antigas.
 *
 * Desde 02/10 a lista da PoolTrack vem da origem (o sistema de cadastro dela),
 * somente leitura, toda noite e no "sincronizar agora" do admin. O bloco da
 * sincronização mostra a última rodada; técnico sincronizado tem nome,
 * documento e cidades da origem (não se editam aqui). São ~1.800 nomes: a tela
 * mostra os ativos por padrão e desenha no máximo MAX_ROWS linhas.
 */
import { useEffect, useRef, useState, type FormEvent } from 'react';
import { usePermissions } from '@/auth/use-permissions';
import { SgrIntegrationCard } from '@/features/sgr/SgrIntegrationCard';
import { useSgr } from '@/features/sgr/sgr-queries';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { CalendarOff, Pencil, Plus, RefreshCw, Trash2 } from 'lucide-react';
import {
  TECHNICIAN_DEFAULT_DAILY_CAPACITY,
  TECHNICIAN_SYNC_ERROR_LABELS,
  type Technician,
  type TechnicianAbsence,
  type TechnicianSyncRun,
} from '@erp/shared';
import { api } from '@/lib/api-client';
import { formatDateTime } from '@/lib/utils';
import { useAuthUser } from '@/auth/use-auth';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Input, Label, Textarea } from '@/components/ui/input';
import { Switch } from '@/components/ui/switch';
import { Badge, Dialog, EmptyState, Skeleton, Spinner } from '@/components/ui/misc';
import { DeskRowAction } from '@/features/desks/desk-stages-section';
import {
  matchesText,
  normalizeText,
  SettingsBreadcrumb,
  SettingsSearch,
} from '@/features/settings/settings-ui';
import {
  formatIsoDate,
  serviceOrderErrorMessage,
  technicianAbsencesQuery,
  techniciansQuery,
  technicianSyncStatusQuery,
  TECHNICIANS_KEY,
  useStartTechnicianSync,
} from './service-orders-queries';

/** teto de linhas desenhadas — a busca refina o resto */
const MAX_ROWS = 200;

export function TechniciansPage() {
  const user = useAuthUser();
  const isAdmin = user?.role === 'admin';
  // spec 032: a integração do SGR e os pontos de venda — de quem tem clients.manage
  const { can, ready } = usePermissions();
  const sgr = useSgr();
  const listQ = useQuery(techniciansQuery());
  const [query, setQuery] = useState('');
  const [showInactive, setShowInactive] = useState(false);
  const [editing, setEditing] = useState<{ technician: Technician | null } | null>(null);
  const [absencesOf, setAbsencesOf] = useState<Technician | null>(null);

  const q = normalizeText(query.trim());
  const all = listQ.data ?? [];
  const inactiveCount = all.filter((t) => !t.active).length;
  const list = all.filter(
    (t) =>
      (showInactive || t.active) &&
      (q === '' ? true : matchesText(`${t.name} ${t.cities.join(' ')} ${t.region ?? ''} ${t.phone ?? ''}`, q))
  );
  const shown = list.slice(0, MAX_ROWS);

  return (
    <div className="rounded-lg border bg-card p-4 shadow-xs sm:p-5">
      <SettingsBreadcrumb page="Técnicos" />
      <div className="mt-2 flex flex-wrap items-center gap-3">
        <h1 className="text-xl leading-7 font-semibold">Técnicos</h1>
        <div className="ml-auto flex items-center gap-2">
          <SettingsSearch value={query} onChange={setQuery} label="Buscar técnico" />
          {isAdmin ? (
            <Button size="sm" className="h-9" onClick={() => setEditing({ technician: null })}>
              <Plus className="size-4" strokeWidth={2.5} />
              Técnico
            </Button>
          ) : null}
        </div>
      </div>
      <p className="mt-1 text-sm text-muted-foreground">
        Quem atende as ordens de serviço e os tickets em campo. Sem cidades cadastradas, o técnico
        aparece para qualquer cidade.
      </p>

      {isAdmin ? <TechnicianSyncCard /> : null}
      {ready && can('clients.manage') && sgr.status?.available ? <SgrIntegrationCard status={sgr.status} /> : null}

      <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-2 text-sm text-muted-foreground">
        <label className="flex cursor-pointer items-center gap-2">
          <Checkbox
            checked={showInactive}
            onCheckedChange={(v) => setShowInactive(v === true)}
            aria-label="Mostrar inativos"
          />
          Mostrar inativos{inactiveCount > 0 ? ` (${inactiveCount.toLocaleString('pt-BR')})` : ''}
        </label>
        {listQ.data ? (
          <span>
            {list.length > shown.length
              ? `Mostrando ${shown.length.toLocaleString('pt-BR')} de ${list.length.toLocaleString('pt-BR')} — refine a busca`
              : `${list.length.toLocaleString('pt-BR')} técnico${list.length === 1 ? '' : 's'}`}
          </span>
        ) : null}
      </div>

      <div className="mt-2">
        {listQ.isPending ? (
          <div className="space-y-2" aria-busy>
            {Array.from({ length: 4 }).map((_, i) => (
              <Skeleton key={i} className="h-10 w-full" />
            ))}
          </div>
        ) : listQ.isError ? (
          <EmptyState message={`Erro ao carregar técnicos: ${serviceOrderErrorMessage(listQ.error)}`} />
        ) : list.length === 0 ? (
          <EmptyState
            className="h-52"
            message={q !== '' ? 'Nenhum técnico encontrado' : 'Nenhum técnico cadastrado'}
          />
        ) : (
          <div className="overflow-x-auto rounded-md border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-muted text-left text-[13px]">
                  <th className="h-10 px-4 font-semibold">Nome</th>
                  <th className="h-10 px-4 font-semibold">Cidades</th>
                  <th className="h-10 px-4 font-semibold">Região</th>
                  <th className="h-10 px-4 font-semibold">Telefone</th>
                  <th className="h-10 w-28 px-4 font-semibold">OS por dia</th>
                  <th className="h-10 w-28 px-4 font-semibold">Status</th>
                  <th className="h-10 w-28 px-4">
                    <span className="sr-only">Ações</span>
                  </th>
                </tr>
              </thead>
              <tbody>
                {shown.map((t) => (
                  <tr
                    key={t.id}
                    className="h-11 border-b border-divider transition-colors last:border-0 hover:bg-accent/60"
                  >
                    <td className="max-w-80 px-4">
                      <span className="block truncate font-medium" title={t.name}>
                        {t.name}
                      </span>
                      {t.sgrId !== null ? (
                        <span className="block text-xs text-muted-foreground">
                          Sincronizado · código {t.sgrId}
                          {t.sgrStatus ? ` · ${t.sgrStatus}` : ''}
                        </span>
                      ) : null}
                    </td>
                    <td className="max-w-64 truncate px-4 text-muted-foreground">
                      {t.cities.length > 0 ? t.cities.join(', ') : 'Qualquer cidade'}
                    </td>
                    <td className="max-w-48 truncate px-4 text-muted-foreground">{t.region ?? '—'}</td>
                    <td className="px-4 text-muted-foreground">{t.phone ?? '—'}</td>
                    <td className="px-4 tabular-nums">{t.dailyCapacity}</td>
                    <td className="px-4">
                      <Badge variant={t.active ? 'success' : 'default'}>
                        {t.active ? 'Ativo' : 'Inativo'}
                      </Badge>
                    </td>
                    <td className="px-4">
                      <div className="flex items-center justify-end gap-0.5">
                        <DeskRowAction label="Ausências" onClick={() => setAbsencesOf(t)}>
                          <CalendarOff className="size-3.5" />
                        </DeskRowAction>
                        {isAdmin ? (
                          <DeskRowAction
                            label="Editar técnico"
                            onClick={() => setEditing({ technician: t })}
                          >
                            <Pencil className="size-3.5" />
                          </DeskRowAction>
                        ) : null}
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {editing ? (
        <TechnicianDialog technician={editing.technician} onClose={() => setEditing(null)} />
      ) : null}
      {absencesOf ? (
        <AbsencesDialog
          technician={absencesOf}
          canEdit={isAdmin}
          onClose={() => setAbsencesOf(null)}
        />
      ) : null}
    </div>
  );
}

/** "1.836 lidos · 12 novos · …" — o total lido e o que não é zero */
function syncCountsText(run: TechnicianSyncRun): string {
  const c = run.counts;
  if (!c) return '';
  const n = (v: number) => v.toLocaleString('pt-BR');
  const parts = [`${n(c.read)} lidos`];
  if (c.created) parts.push(`${n(c.created)} novos`);
  if (c.updated) parts.push(`${n(c.updated)} atualizados`);
  if (c.deactivated) parts.push(`${n(c.deactivated)} desativados`);
  parts.push(`${n(c.unchanged)} sem mudança`);
  if (c.skipped) parts.push(`${n(c.skipped)} linhas ilegíveis`);
  return parts.join(' · ');
}

/**
 * Bloco da sincronização (só admin). O nome da origem vem da API — palavra de
 * sistema de um cliente não é texto do SPA. Tenant que não é o da integração
 * não vê o bloco (`available: false`).
 */
function TechnicianSyncCard() {
  const qc = useQueryClient();
  const statusQ = useQuery(technicianSyncStatusQuery(true));
  const start = useStartTechnicianSync();
  const data = statusQ.data;
  const lastStatus = data?.available ? data.lastRun?.status : undefined;

  // a rodada termina em background: quando sai de "running", a lista mudou
  const wasRunning = useRef(false);
  useEffect(() => {
    if (lastStatus === 'running') wasRunning.current = true;
    else if (wasRunning.current) {
      wasRunning.current = false;
      void qc.invalidateQueries({ queryKey: [...TECHNICIANS_KEY, 'list'] });
    }
  }, [lastStatus, qc]);

  if (!data || !data.available) return null;
  const run = data.lastRun;
  const running = run?.status === 'running';

  return (
    <section className="mt-4 rounded-md border bg-muted/30 p-3 text-sm" aria-label="Sincronização de técnicos">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1 space-y-1">
          <p className="font-semibold">{`Sincronização com o ${data.sourceName}`}</p>
          <p className="text-muted-foreground">
            {data.enabled
              ? `Lista lida do ${data.sourceName} ${data.scheduleLabel}. Somente leitura: o ERP nunca escreve no ${data.sourceName}.`
              : `Desligada neste ambiente — falta configurar: ${data.missingConfig.join(', ')}.`}
          </p>
        </div>
        {data.enabled ? (
          <Button
            size="sm"
            variant="outline"
            className="h-9"
            disabled={running || start.isPending}
            onClick={() => start.mutate()}
          >
            {running || start.isPending ? <Spinner className="size-4" /> : <RefreshCw className="size-4" />}
            {`Sincronizar técnicos do ${data.sourceName} agora`}
          </Button>
        ) : null}
      </div>

      <div className="mt-2 space-y-0.5 text-muted-foreground">
        {run ? (
          <p className="flex flex-wrap items-center gap-x-2 gap-y-1">
            <span>Última rodada: {formatDateTime(run.startedAt)}</span>
            <span>
              {run.trigger === 'manual'
                ? `· manual${run.triggeredBy ? `, por ${run.triggeredBy.name}` : ''}`
                : '· agendada'}
            </span>
            {run.status === 'running' ? (
              <Badge variant="info">Sincronizando…</Badge>
            ) : run.status === 'ok' ? (
              <Badge variant="success">Concluída</Badge>
            ) : (
              <Badge variant="destructive">Falhou</Badge>
            )}
          </p>
        ) : (
          <p>Nenhuma rodada ainda.</p>
        )}
        {run?.status === 'ok' ? <p>{syncCountsText(run)}</p> : null}
        {run?.status === 'error' && run.errorKind ? (
          <p className="text-destructive">
            {TECHNICIAN_SYNC_ERROR_LABELS[run.errorKind]} Nada foi alterado no cadastro.
          </p>
        ) : null}
        {data.lastSuccessAt && run?.status !== 'ok' ? (
          <p>Última sincronização com sucesso: {formatDateTime(data.lastSuccessAt)}</p>
        ) : null}
        {start.error ? <p className="text-destructive">{serviceOrderErrorMessage(start.error)}</p> : null}
      </div>
    </section>
  );
}

function TechnicianDialog({
  technician,
  onClose,
}: {
  technician: Technician | null;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const [name, setName] = useState(technician?.name ?? '');
  const [phone, setPhone] = useState(technician?.phone ?? '');
  const [document, setDocument] = useState(technician?.document ?? '');
  // uma cidade por linha: "Belo Horizonte, MG" tem vírgula dentro
  const [cities, setCities] = useState((technician?.cities ?? []).join('\n'));
  const [capacity, setCapacity] = useState(
    String(technician?.dailyCapacity ?? TECHNICIAN_DEFAULT_DAILY_CAPACITY)
  );
  const [active, setActive] = useState(technician?.active ?? true);
  const [notes, setNotes] = useState(technician?.notes ?? '');

  // técnico da origem: nome, documento e cidades são dela — não se editam
  // aqui (a API recusa com 409) e nem vão no corpo
  const synced = technician?.sgrId != null;

  const save = useMutation({
    mutationFn: async () => {
      const body = {
        ...(synced ? {} : { name: name.trim(), document, cities: cities.split('\n') }),
        phone,
        dailyCapacity: Number(capacity),
        active,
        notes,
      };
      const res = technician
        ? await api<Technician>(`/technicians/${technician.id}`, { method: 'PATCH', body })
        : await api<Technician>('/technicians', { method: 'POST', body });
      if (res.error) throw res.error;
      return res.data;
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: TECHNICIANS_KEY });
      onClose();
    },
  });

  const capacityValid = /^\d+$/.test(capacity) && Number(capacity) >= 1 && Number(capacity) <= 50;
  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim() || !capacityValid) return;
    save.mutate();
  };

  return (
    <Dialog open onClose={onClose} title={technician ? 'Editar técnico' : 'Novo técnico'}>
      <form onSubmit={submit} className="space-y-4">
        {synced ? (
          <p className="rounded-md border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">
            Técnico sincronizado (código {technician?.sgrId}
            {technician?.sgrStatus ? `, ${technician.sgrStatus}` : ''}). Nome, documento e cidades
            vêm da origem e são atualizados a cada sincronização; telefone, capacidade, ativo e
            observações são do ERP.
          </p>
        ) : null}
        <div className="space-y-1.5">
          <Label htmlFor="tech-name">
            Nome <span className="text-destructive">*</span>
          </Label>
          <Input
            id="tech-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            required
            autoFocus={!synced}
            disabled={synced}
          />
        </div>
        <div className="grid gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="tech-phone">Telefone</Label>
            <Input id="tech-phone" value={phone} onChange={(e) => setPhone(e.target.value)} />
          </div>
          <div className="space-y-1.5">
            <Label htmlFor="tech-document">Documento (CPF/CNPJ)</Label>
            <Input
              id="tech-document"
              value={document}
              onChange={(e) => setDocument(e.target.value)}
              disabled={synced}
            />
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tech-cities">Cidades que atende (uma por linha)</Label>
          <Textarea
            id="tech-cities"
            rows={3}
            value={cities}
            onChange={(e) => setCities(e.target.value)}
            placeholder={'Belo Horizonte\nContagem'}
            disabled={synced}
          />
          <p className="text-xs text-muted-foreground">
            Vazio = atende qualquer cidade. Acento e maiúsculas não importam.
          </p>
        </div>
        <div className="grid items-end gap-3 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label htmlFor="tech-capacity">OS por dia</Label>
            <Input
              id="tech-capacity"
              type="number"
              min={1}
              max={50}
              value={capacity}
              onChange={(e) => setCapacity(e.target.value)}
            />
          </div>
          <div className="flex h-9 items-center gap-2">
            <Switch id="tech-active" aria-label="Técnico ativo" checked={active} onCheckedChange={setActive} />
            <Label htmlFor="tech-active" className="cursor-pointer">
              Ativo
            </Label>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label htmlFor="tech-notes">Observações</Label>
          <Textarea id="tech-notes" rows={2} value={notes} onChange={(e) => setNotes(e.target.value)} />
        </div>
        {save.error ? (
          <p className="text-sm text-destructive">{serviceOrderErrorMessage(save.error)}</p>
        ) : null}
        <div className="flex justify-end gap-2 pt-1">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="submit" size="sm" disabled={save.isPending || !name.trim() || !capacityValid}>
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </div>
      </form>
    </Dialog>
  );
}

function AbsencesDialog({
  technician,
  canEdit,
  onClose,
}: {
  technician: Technician;
  canEdit: boolean;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const absencesQ = useQuery(technicianAbsencesQuery(technician.id));
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [reason, setReason] = useState('');

  // ausência muda a disponibilidade: invalida o prefixo inteiro dos técnicos
  const invalidate = () => void qc.invalidateQueries({ queryKey: TECHNICIANS_KEY });

  const add = useMutation({
    mutationFn: async () => {
      const res = await api<TechnicianAbsence>(`/technicians/${technician.id}/absences`, {
        method: 'POST',
        body: { startDate, endDate: endDate || startDate, reason },
      });
      if (res.error) throw res.error;
    },
    onSuccess: () => {
      setStartDate('');
      setEndDate('');
      setReason('');
      invalidate();
    },
  });

  const remove = useMutation({
    mutationFn: async (absenceId: number) => {
      const res = await api<void>(`/technicians/${technician.id}/absences/${absenceId}`, {
        method: 'DELETE',
      });
      if (res.error) throw res.error;
    },
    onSuccess: invalidate,
  });

  const submit = (e: FormEvent) => {
    e.preventDefault();
    if (!startDate) return;
    add.mutate();
  };

  const absences = absencesQ.data ?? [];

  return (
    <Dialog open onClose={onClose} title={`Ausências — ${technician.name}`}>
      <div className="space-y-4">
        {absencesQ.isPending ? (
          <Skeleton className="h-16 w-full" />
        ) : absences.length === 0 ? (
          <EmptyState className="h-20" message="Nenhuma ausência cadastrada" />
        ) : (
          <ul className="divide-y rounded-md border text-sm">
            {absences.map((a) => (
              <li key={a.id} className="flex items-center gap-3 px-3 py-2">
                <span className="tabular-nums">
                  {formatIsoDate(a.startDate)}
                  {a.endDate !== a.startDate ? ` a ${formatIsoDate(a.endDate)}` : ''}
                </span>
                <span className="flex-1 truncate text-muted-foreground">{a.reason ?? ''}</span>
                {canEdit ? (
                  <DeskRowAction
                    label="Remover ausência"
                    destructive
                    disabled={remove.isPending}
                    onClick={() => remove.mutate(a.id)}
                  >
                    <Trash2 className="size-3.5" />
                  </DeskRowAction>
                ) : null}
              </li>
            ))}
          </ul>
        )}

        {canEdit ? (
          <form onSubmit={submit} className="space-y-3 rounded-md border bg-muted/30 p-3">
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="space-y-1.5">
                <Label htmlFor="abs-start">Início</Label>
                <Input id="abs-start" type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} required />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="abs-end">Fim (vazio = um dia)</Label>
                <Input id="abs-end" type="date" value={endDate} min={startDate} onChange={(e) => setEndDate(e.target.value)} />
              </div>
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="abs-reason">Motivo</Label>
              <Input id="abs-reason" value={reason} onChange={(e) => setReason(e.target.value)} placeholder="Folga, férias…" />
            </div>
            {add.error ? (
              <p className="text-sm text-destructive">{serviceOrderErrorMessage(add.error)}</p>
            ) : null}
            <div className="flex justify-end">
              <Button type="submit" size="sm" disabled={add.isPending || !startDate}>
                Adicionar ausência
              </Button>
            </div>
          </form>
        ) : null}
        {remove.error ? (
          <p className="text-sm text-destructive">{serviceOrderErrorMessage(remove.error)}</p>
        ) : null}
        <div className="flex justify-end">
          <Button type="button" variant="outline" size="sm" onClick={onClose}>
            Fechar
          </Button>
        </div>
      </div>
    </Dialog>
  );
}
